import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { matchFieldsValidator } from 'src/app/validators/match-field.validators';
import { NotificationService } from 'src/app/core/services/notification.service';
import { MessageModule } from 'primeng/message';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../../services/auth-service/auth.service';
import { firstValueFrom } from 'rxjs';
import { PasswordModule } from 'primeng/password';
import { DividerModule } from 'primeng/divider';
import { passwordStrengthValidator } from 'src/app/validators/password-strength.validators';
import { PasswordStrengthIndicatorComponent } from 'src/app/shared/components/password-strength-indicator/password-strength-indicator.component';

@Component({
  selector: 'app-reset-password',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    RouterModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ToastModule,
    MessageModule,
    TranslocoModule,
    PasswordModule,
    DividerModule,
    PasswordStrengthIndicatorComponent,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit {
  route = inject(ActivatedRoute);
  fb = inject(FormBuilder);
  authService = inject(AuthService);
  transloco = inject(TranslocoService);
  notificationService = inject(NotificationService);
  router = inject(Router);
  cdr = inject(ChangeDetectorRef);
  loading = false;
  token = this.route.snapshot.queryParams['id'];
  passwordForm = this.fb.group(
    {
      newPassword: ['', [Validators.required, passwordStrengthValidator()]],
      confirmPassword: ['', Validators.required],
    },
    { validators: matchFieldsValidator('newPassword', 'confirmPassword') },
  );
  // The link is usable until the backend proves otherwise. Its real validity
  // (opaque single-use invitation token vs short-lived forgot-password JWT) is
  // decided server-side on submit, so we no longer decode/expire it client-side.
  isLinkValid = true;
  linkErrorKey = 'resetPasswordPage.invalidLink';
  isNewPasswordVisible = false;
  isConfirmPasswordVisible = false;

  /**
   * Backend token-rejection codes that are terminal for the current link: when
   * one of these comes back we disable the form and surface the reason in the
   * banner. Anything else (network / server error) is treated as retryable.
   */
  private readonly linkErrorMessages: Record<string, string> = {
    E_INVITATION_ALREADY_USED: 'resetPasswordPage.linkAlreadyUsed',
    E_INVITATION_EXPIRED: 'resetPasswordPage.linkExpired',
    E_INVALID_RESET_PASSWORD_TOKEN: 'resetPasswordPage.invalidLink',
    E_USER_NOT_FOUND: 'resetPasswordPage.invalidLink',
  };

  ngOnInit() {
    // Only guard against a completely missing token; the backend validates the rest.
    if (!this.token) {
      this.isLinkValid = false;
    }
  }

  sendNewPassword() {
    if (this.passwordForm.valid) {
      this.loading = true;
      const password = this.passwordForm.get('newPassword')!.value!;
      this.authService.resetPassword(this.token, password).subscribe({
        next: async () => {
          this.loading = false;
          const message = await firstValueFrom(
            this.transloco.selectTranslate('resetPasswordPage.resetSuccessful'),
          );
          this.notificationService.success(message);
          setTimeout(() => {
            this.router.navigate(['/auth/login']);
          }, 2000);
        },
        error: async (error: HttpErrorResponse) => {
          this.loading = false;
          const code = error.error?.code as string | undefined;
          const linkErrorKey = code ? this.linkErrorMessages[code] : undefined;
          if (linkErrorKey) {
            // Terminal token error: lock the form and explain why in the banner.
            this.linkErrorKey = linkErrorKey;
            this.isLinkValid = false;
            return;
          }
          // Transient failure: let the user retry with the same link.
          const message = await firstValueFrom(
            this.transloco.selectTranslate('resetPasswordPage.resetFailed'),
          );
          this.notificationService.error(message);
          console.error('Error resetting password:', error);
        },
      });
    }
  }
  
  goToLogin() {
    this.router.navigate(['/auth/login']);
  }

  get notMatchingError() {
    return this.passwordForm.hasError('notMatching');
  }

  isInvalid(controlName: string): boolean {
    const control = this.passwordForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
  
  getControlErrors(controlName: string): string[] {
    const control = this.passwordForm.get(controlName);
    return control && control.errors ? Object.keys(control.errors) : [];
  }

  toggleNewPasswordVisibility(): void {
    this.isNewPasswordVisible = !this.isNewPasswordVisible;
  }

  toggleConfirmPasswordVisibility(): void {
    this.isConfirmPasswordVisible = !this.isConfirmPasswordVisible;
  }
}
